import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

type Request = { text: string; id: number };

type State = {
  request: Request | null;
  set: (text: string) => void;
  clear: () => void;
};

// Counts requests for their whole life, not the life of the one being held: a
// request is cleared once taken, so numbering from whatever is pending would
// hand the same id out again and make a repeated ask look like one already
// delivered.
let lastRequestId = 0;

// A composer's text is local to a mounted chat, and the add-content store's
// `initialText` only lands when a chat is created, so neither reaches a composer
// already on screen. A surface outside the chat that wants to fill one leaves
// the request here instead. The id makes the same text asked for twice a second
// request rather than a no-op.
const useComposerPrefillStore = create<State>((set) => ({
  request: null,
  set: (text) => set({ request: { text, id: ++lastRequestId } }),
  clear: () => set({ request: null }),
}));

/**
 * Fills the chat composer from outside the chat. Left pending until a composer
 * that can take it is on screen, so asking before the panel has opened still
 * lands once it does.
 */
export const requestComposerPrefill = (text: string) =>
  useComposerPrefillStore.getState().set(text);

/**
 * Abandons a request no composer took. The surface that asked owns this: a
 * request left pending is global and outlives the page that made it, so it
 * would otherwise surface in a composer on some unrelated planner later.
 */
export const clearComposerPrefill = () =>
  useComposerPrefillStore.getState().clear();

/**
 * Takes a pending prefill, if this composer is one that should accept it. Pass
 * `undefined` to decline: the request is left for a composer that can, rather
 * than swallowed.
 */
export const useComposerPrefill = (
  onPrefill: ((text: string) => void) | undefined
) => {
  const request = useComposerPrefillStore((state) => state.request);
  const clear = useComposerPrefillStore((state) => state.clear);
  // What has already been handed over. StrictMode replays an effect with the
  // render's own captured request, so clearing the store is not enough on its
  // own to stop the same one being delivered twice.
  const delivered = useRef<number | null>(null);
  // A chat seeds its composer in its own mount effect, which runs after this
  // child's, so a request delivered on the composer's first commit is wiped.
  // Waiting a render lands it after the chat has finished seeding.
  const [isMounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (
      !isMounted ||
      !request ||
      !onPrefill ||
      delivered.current === request.id
    ) {
      return;
    }

    delivered.current = request.id;
    onPrefill(request.text);
    clear();
  }, [isMounted, request, onPrefill, clear]);
};
